import Swiper from 'swiper/bundle';

if(!customElements.get('lsg-3-cards')) {
    class LSG3Cards extends HTMLElement {
        constructor() {
            super();
            this.classes = {
                swiper: 'js-lsg-3-cards-slider',
                swiperSlide: 'swiper-slide',
                wrapper: 'swiper-wrapper',
                navigation: {
                    pagination: 'js-lsg-3-cards-slider-pagination',
                    prev: 'js-lsg-3-cards-slider-prev',
                    next: 'js-lsg-3-cards-slider-next',
                }
            }

            this.elements = {
                swiper: this.querySelector(`.${this.classes.swiper}`),
                slides: this.querySelectorAll(`.${this.classes.swiperSlide}`),
                wrapper: this.querySelector(`.${this.classes.wrapper}`),
                navigation: {
                    pagination: this.querySelector(`.${this.classes.navigation.pagination}`),
                    prev: this.querySelector(`.${this.classes.navigation.prev}`),
                    next: this.querySelector(`.${this.classes.navigation.next}`),
                }
            }

            // Single source of truth for breakpoint configurations
            this.breakpointConfigs = {
                mobile: { slidesPerView: 1, spaceBetween: 10 },
                tablet: { slidesPerView: 2, spaceBetween: 20 },
                desktop: { slidesPerView: 3, spaceBetween: 37 }
            };

            this.resizeHandler = this.handleResize.bind(this);
            this.currentBreakpoint = this.getCurrentBreakpoint();
        }

        /**
         * Called when the element is added to the DOM
         * Initializes the component and sets up event listeners
         */
        connectedCallback() {
            if(this.checkIfEmpty()) return;
            this.init();
            window.addEventListener('resize', this.resizeHandler);
        }

        /**
         * Called when the element is removed from the DOM
         * Cleans up the swiper instance and event listeners
         */
        disconnectedCallback() {
            if(this.swiper) {
                this.swiper.destroy();
            }
            window.removeEventListener('resize', this.resizeHandler);
            if(this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
        }

        /**
         * Checks if the component has content to display
         * @returns {boolean} True if empty, false if has content
         */
        checkIfEmpty() {
            return false;
        }

        /**
         * Determines the current breakpoint based on window width
         * @returns {string} 'mobile', 'tablet', or 'desktop'
         */
        getCurrentBreakpoint() {
            const width = window.innerWidth;
            if (width < 768) return 'mobile'; // md breakpoint
            if (width < 1024) return 'tablet'; // lg breakpoint
            return 'desktop';
        }

        /**
         * Gets the minimum number of slides required to initialize slider for a given breakpoint
         * @param {string} breakpoint - The breakpoint to check
         * @returns {number} Minimum slides required
         */
        getRequiredSlidesForBreakpoint(breakpoint) {
            switch(breakpoint) {
                case 'mobile': return this.breakpointConfigs.mobile.slidesPerView;
                case 'tablet': return this.breakpointConfigs.tablet.slidesPerView;
                default: return this.breakpointConfigs.desktop.slidesPerView;
            }
        }

        /**
         * Gets the slider configuration for the current breakpoint
         * @returns {Object} Object with slidesPerView and spaceBetween
         */
        getSliderConfig() {
            const currentBreakpoint = this.getCurrentBreakpoint();
            
            return this.breakpointConfigs[currentBreakpoint];
        }

        /**
         * Main initialization method
         * Checks if slider should initialize and calls appropriate method
         */
        init() {
            if(!this.shouldInit()) return;
            this.initSwiper();
        }

        /**
         * Determines if the slider should be initialized based on current breakpoint and slide count
         * @returns {boolean} True if slider should initialize, false otherwise
         */
        shouldInit() {
            if(!this.elements.swiper || !this.elements.slides.length) return false;
            
            const slidesCount = this.elements.slides.length;
            const currentBreakpoint = this.getCurrentBreakpoint();
            const requiredSlides = this.getRequiredSlidesForBreakpoint(currentBreakpoint);
            
            return slidesCount > requiredSlides;
        }

        /**
         * Handles window resize events
         * Reinitializes slider if breakpoint changes or slider state should change
         */
        handleResize() {
            if(this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
                const newBreakpoint = this.getCurrentBreakpoint();
                const shouldInitialize = this.shouldInit();
                
                // Only reinitialize if breakpoint changed or slider state should change
                if (newBreakpoint !== this.currentBreakpoint || 
                    (shouldInitialize && !this.swiper) || 
                    (!shouldInitialize && this.swiper)) {
                    
                    this.currentBreakpoint = newBreakpoint;
                    
                    if(shouldInitialize && !this.swiper) {
                        this.initSwiper();
                    } else if(!shouldInitialize && this.swiper) {
                        this.swiper.destroy(true, true);
                        this.swiper = null;
                    } else if(shouldInitialize && this.swiper) {
                        // Update existing swiper configuration
                        this.updateSwiperConfig();
                    }
                }
            }, 300);
        }

        /**
         * Updates the configuration of an existing swiper instance
         * Called when breakpoint changes but slider should remain active
         */
        updateSwiperConfig() {
            if (!this.swiper) return;
            
            const config = this.getSliderConfig();
            this.swiper.params.slidesPerView = config.slidesPerView;
            this.swiper.params.spaceBetween = config.spaceBetween;
            this.swiper.update();
        }

        /**
         * Initializes the Swiper slider with current breakpoint configuration
         * Sets up navigation, pagination, and responsive breakpoints
         */
        initSwiper() {
            const config = this.getSliderConfig();

            this.swiper = new Swiper(this.elements.swiper, {
                slidesPerView: config.slidesPerView,
                spaceBetween: config.spaceBetween,
                watchOverflow: true,
                loop: true,
                navigation: {
                    nextEl: this.elements.navigation.next,
                    prevEl: this.elements.navigation.prev,
                },
                pagination: {
                    el: this.elements.navigation.pagination,
                    clickable: true
                },
                breakpoints: {
                    320: {
                        autoHeight: true,
                        ...this.breakpointConfigs.mobile
                    },
                    768: {
                        autoHeight: false,
                        ...this.breakpointConfigs.tablet
                    },
                    1024: {
                        ...this.breakpointConfigs.desktop
                    }
                }
            });
        }
    }

    customElements.define('lsg-3-cards', LSG3Cards);
}